// Dvip code

// src/api/storage.ts
import { apiClient } from "@/api/client";

export type UploadSubject = "user_avatar" | "media_agency_logo";

export interface PresignResponse {
  success: boolean;
  data: {
    method: "PUT";
    url: string;
    headers: Record<string, string>;
    key: string;
    expires_at: string;
  };
}

export async function presignUpload(subject: UploadSubject, file: File) {
  const ext = (file.name.split(".").pop() || "bin").toLowerCase();
  const mime = file.type || "application/octet-stream";

  const { data } = await apiClient.post<PresignResponse>("/storage", {
    action: "presign",
    subject,
    ext,
    mime,
    size: file.size,
  });

  if (!data?.success) throw new Error("Failed to presign upload.");
  return { ...data.data, mime };
}

// Upload to S3 with progress + cancel support
export async function putToS3WithProgress(
  url: string,
  headers: Record<string, string>,
  file: File,
  onProgress?: (pct: number) => void,
  signal?: AbortSignal
) {
  // fetch doesn't expose upload progress; use XHR for this one call
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);

    Object.entries(headers || {}).forEach(([k, v]) => xhr.setRequestHeader(k, v));

    xhr.upload.onprogress = (e) => {
      if (onProgress && e.lengthComputable) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`S3 upload failed: ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error("Network error during S3 upload."));
    xhr.onabort = () => reject(new Error("Upload aborted."));

    if (signal) {
      if (signal.aborted) return xhr.abort();
      signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }

    xhr.send(file);
  });
}

export async function confirmUpload(subject: UploadSubject, key: string, mime: string) {
  const { data } = await apiClient.post("/storage", {
    action: "confirm",
    subject,
    key,
    mime,
  });
  if (!data?.success) throw new Error("Failed to confirm upload.");
}

export async function signGet(subject: UploadSubject, key: string, ttlMinutes = 30) {
  const { data } = await apiClient.post("/storage", {
    action: "sign-get",
    subject,
    key,
    ttl_minutes: ttlMinutes,
  });
  if (!data?.success) throw new Error("Failed to sign GET URL.");
  return data.data.url as string;
}
